import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { erStorby } from "@/components/oversiktConstants";
import { useSideTittel } from "@/hooks/useSideTittel";
import { DelKnapp } from "@/components/DelKnapp";
import { LivsopphaldSection } from "@/components/LivsopphaldSection";
import { FastlegeSection } from "@/components/FastlegeSection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Landmark,
  PiggyBank,
  AlertTriangle,
  User,
  LogOut,
  LogIn,
  Settings,
  Users,
  RefreshCw,
  BarChart3,
  Edit2,
  Save,
  X,
  Database,
  Shield,
  ExternalLink,
  HardHat,
  Wallet,
  Trophy,
  HeartPulse,
  Baby,
  Briefcase,
  Home,
} from "lucide-react";
import ArticleSection from "@/components/ArticleSection";
import BreakdownSection from "@/components/BreakdownSection";
import RevenueSection from "@/components/RevenueSection";
import GebyrSection from "@/components/GebyrSection";
import BokostnadSection from "@/components/BokostnadSection";
import LeieSection from "@/components/LeieSection";
import BoligpriserSection from "@/components/BoligpriserSection";
import BoligOverview from "@/components/BoligOverview";
import SectionNav from "@/components/SectionNav";
import LevekostnadCard from "@/components/LevekostnadCard";
import ArbeidSection from "@/components/ArbeidSection";
import PendlingSection from "@/components/PendlingSection";
import { HusholdningSection, HOUSEHOLD_TYPE_LABELS } from "@/components/HusholdningSection";
import KommuneCharts from "@/components/KommuneCharts";
import KommuneDemographics from "@/components/KommuneDemographics";
import AlderSection from "@/components/AlderSection";
import ArbeidskapitalChart from "@/components/ArbeidskapitalChart";
import PremieavvikChart from "@/components/PremieavvikChart";
import FondSection from "@/components/FondSection";
import EiendomSection from "@/components/EiendomSection";
import FinanskostnadSection from "@/components/FinanskostnadSection";
import VarselSection from "@/components/VarselSection";
import UlikskapSection from "@/components/UlikskapSection";
import TjenesterSection from "@/components/TjenesterSection";
import EndringarNotice from "@/components/EndringarNotice";
import { setEndringsMerke, clearEndringsMerke } from "@/components/endringMerke";
import DisposisjonsfondChart from "@/components/DisposisjonsfondChart";
import RangeringSection from "@/components/RangeringSection";
import KorttidSection from "@/components/KorttidSection";
import FinanceDataTable from "@/components/FinanceDataTable";
import { formatNumber, dec, num } from "@/lib/formatters";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SectionHeading = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-6 flex items-start gap-3">
    <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#103766] text-white"><Icon className="w-4 h-4" /></span>
    <div>
      <h2 className="text-lg md:text-xl font-medium text-[#1F2A36]">{title}</h2>
      <p className="text-sm text-[#4B647F]">{subtitle}</p>
    </div>
  </div>
);

const KommuneView = () => {
  const { kommuneCode } = useParams();
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [kommune, setKommune] = useState(null);
  const [financeData, setFinanceData] = useState([]);
  const kommuneNamn = kommune?.kommune_name;
  useSideTittel(
    kommuneNamn ? `${kommuneNamn} kommune – økonomi, gjeld og tenester` : "Kommunetal",
    kommuneNamn
      ? `Tal for ${kommuneNamn} kommune: lånegjeld per innbyggjar, driftsresultat, eigedomsskatt, kommunale gebyr, barnehage, skule og eldreomsorg – ` +
        `samanlikna med ${kommune?.storby ? "dei andre storbyane" : kommune?.fylke_name || "fylket"} og landet. Oppdaterte tal frå KOSTRA og SSB.`
      : "Nøkkeltal for norske kommunar frå KOSTRA og SSB."
  );

  const [populationData, setPopulationData] = useState([]);
  const [demographicsData, setDemographicsData] = useState(null);
  const [oversiktData, setOversiktData] = useState([]);
  const [gebyrData, setGebyrData] = useState([]);
  const [bokostnadData, setBokostnadData] = useState(null);
  const [gebyrAverages, setGebyrAverages] = useState(null);
  const [leieData, setLeieData] = useState(null);
  const [boligpriserData, setBoligpriserData] = useState(null);
  const [husholdningData, setHusholdningData] = useState(null);
  const [levekostnadData, setLevekostnadData] = useState(null);
  const [arbeidData, setArbeidData] = useState(null);
  const [pendlingData, setPendlingData] = useState(null);
  const [alderData, setAlderData] = useState(null);
  const [arbeidskapitalData, setArbeidskapitalData] = useState(null);
  const [disposisjonsfondData, setDisposisjonsfondData] = useState(null);
  const [rangeringData, setRangeringData] = useState(null);
  const [premieavvikData, setPremieavvikData] = useState(null);
  const [fondData, setFondData] = useState(null);
  const [varselData, setVarselData] = useState(null);
  const [ulikskapData, setUlikskapData] = useState(null);
  const [livsopphaldData, setLivsopphaldData] = useState(null);
  const [fastlegeData, setFastlegeData] = useState(null);
  const [sokeParams] = useSearchParams();
  const temaFraUrl = sokeParams.get("tema");
  const [tjenesterData, setTjenesterData] = useState(null);
  const [eiendomData, setEiendomData] = useState(null);
  const [finanskostnadData, setFinanskostnadData] = useState(null);
  const [naboarData, setNaboarData] = useState(null);
  const [endringarData, setEndringarData] = useState(null);
  const [korttidData, setKorttidData] = useState(null);
  const latestHouseholdIncome = husholdningData?.income?.slice(-1)[0];
  const incomeBase = latestHouseholdIncome?.alle?.etter_skatt ?? demographicsData?.median_income;
  const incomeLabel = latestHouseholdIncome?.alle?.etter_skatt
    ? `median inntekt etter skatt per hushald ${latestHouseholdIncome.year}`
    : "median samla inntekt per hushald, før skatt";
  const latestPersonEconomy = husholdningData?.economy?.slice(-1)[0];
  const incomeOverride = latestHouseholdIncome
    ? {
        year: latestHouseholdIncome.year,
        median_income: latestHouseholdIncome.alle?.samlet,
        median_income_after_tax: latestHouseholdIncome.alle?.etter_skatt,
        median_income_person: latestPersonEconomy?.year === latestHouseholdIncome.year ? latestPersonEconomy.median_bruttoinntekt : undefined,
      }
    : null;
  const householdTypes = latestHouseholdIncome
    ? Object.entries(HOUSEHOLD_TYPE_LABELS)
        .filter(([key]) => latestHouseholdIncome[key]?.etter_skatt)
        .map(([key, label]) => ({ key, label, income: latestHouseholdIncome[key].etter_skatt }))
    : [];
  const [fylkeRevenueAvg, setFylkeRevenueAvg] = useState(null);
  const averagesFor = (section) =>
    fylkeRevenueAvg
      ? {
          fylkeName: fylkeRevenueAvg.name,
          storby: fylkeRevenueAvg.fylke_code === "storby",
          fylke: fylkeRevenueAvg[section],
          landName: fylkeRevenueAvg.land?.name || "Landet",
          land: fylkeRevenueAvg.land?.[section],
        }
      : null;
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Admin edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ordforar: "", styringsparti: [] });
  const [saving, setSaving] = useState(false);
  
  // KOSTRA data dialog state
  const [kostraDialogOpen, setKostraDialogOpen] = useState(false);

  useEffect(() => {
    if (!temaFraUrl || loading) return undefined;
    let forsok = 0;
    const id = setInterval(() => {
      const el = document.getElementById(temaFraUrl);
      forsok += 1;
      if (el) {
        const nav = document.querySelector('[data-testid="section-nav"]');
        const topp = 64 + (nav?.offsetHeight || 0) + 12;
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - topp, behavior: "smooth" });
      }
      if (el || forsok > 20) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, [temaFraUrl, loading]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      clearEndringsMerke();
      const [kommuneRes, financeRes, populationRes, partiesRes, demographicsRes, oversiktRes, gebyrRes, bokostnadRes] = await Promise.all([
        axios.get(`${API}/kommuner/${kommuneCode}`),
        axios.get(`${API}/finance/${kommuneCode}`),
        axios.get(`${API}/population/${kommuneCode}`),
        axios.get(`${API}/parties`),
        axios.get(`${API}/demographics/${kommuneCode}`),
        axios.get(`${API}/okonomisk-oversikt/${kommuneCode}`).catch(() => ({ data: [] })),
        axios.get(`${API}/gebyrer/${kommuneCode}`).catch(() => ({ data: [] })),
        axios.get(`${API}/bokostnad/${kommuneCode}`).catch(() => ({ data: null })),
      ]);
      setGebyrData(gebyrRes.data);
      setBokostnadData(bokostnadRes.data);
      setKommune(kommuneRes.data);
      setFinanceData(financeRes.data);
      setPopulationData(populationRes.data);
      setParties(partiesRes.data);
      setDemographicsData(demographicsRes.data);
      setOversiktData(oversiktRes.data);
      if (kommuneRes.data?.fylke_code) {
        const storbyKommune = erStorby(kommuneCode);
        const eigeFylke = !!kommuneRes.data.eige_fylke && !storbyKommune;
        const snittKode = storbyKommune ? "storby" : kommuneRes.data.fylke_code;
        if (!eigeFylke) {
          axios.get(`${API}/okonomisk-oversikt/fylke-average/${snittKode}`)
            .then((res) => setFylkeRevenueAvg(res.data))
            .catch(() => setFylkeRevenueAvg(null));
        } else {
          setFylkeRevenueAvg(null);
        }
        axios.get(`${API}/leie/${kommuneCode}`).then((res) => setLeieData(res.data)).catch(() => setLeieData(null));
        axios.get(`${API}/husholdning/${kommuneCode}`).then((res) => setHusholdningData(res.data)).catch(() => setHusholdningData(null));
        axios.get(`${API}/levekostnad/${kommuneCode}`).then((res) => setLevekostnadData(res.data)).catch(() => setLevekostnadData(null));
        axios.get(`${API}/arbeid/${kommuneCode}`).then((res) => setArbeidData(res.data)).catch(() => setArbeidData(null));
        axios.get(`${API}/pendling/${kommuneCode}`).then((res) => setPendlingData(res.data)).catch(() => setPendlingData(null));
        axios.get(`${API}/alder/${kommuneCode}`).then((res) => setAlderData(res.data)).catch(() => setAlderData(null));
        axios.get(`${API}/arbeidskapital/${kommuneCode}`).then((res) => setArbeidskapitalData(res.data)).catch(() => setArbeidskapitalData(null));
        axios.get(`${API}/disposisjonsfond/${kommuneCode}`).then((res) => setDisposisjonsfondData(res.data)).catch(() => setDisposisjonsfondData(null));
        axios.get(`${API}/rangering/${kommuneCode}`).then((res) => setRangeringData(res.data)).catch(() => setRangeringData(null));
        axios.get(`${API}/premieavvik/${kommuneCode}`).then((res) => setPremieavvikData(res.data)).catch(() => setPremieavvikData(null));
        axios.get(`${API}/fond/${kommuneCode}`).then((res) => setFondData(res.data)).catch(() => setFondData(null));
        axios.get(`${API}/varsel/${kommuneCode}`).then((res) => setVarselData(res.data)).catch(() => setVarselData(null));
        axios.get(`${API}/ulikskap/${kommuneCode}`).then((res) => setUlikskapData(res.data)).catch(() => setUlikskapData(null));
        axios.get(`${API}/livsopphald/${kommuneCode}`).then((res) => setLivsopphaldData(res.data)).catch(() => setLivsopphaldData(null));
        axios.get(`${API}/fastlege/${kommuneCode}`).then((res) => setFastlegeData(res.data)).catch(() => setFastlegeData(null));
        axios.get(`${API}/tjenester/${kommuneCode}`).then((res) => setTjenesterData(res.data)).catch(() => setTjenesterData(null));
        axios.get(`${API}/eiendom/${kommuneCode}`).then((res) => setEiendomData(res.data)).catch(() => setEiendomData(null));
        axios.get(`${API}/finanskostnad/${kommuneCode}`).then((res) => setFinanskostnadData(res.data)).catch(() => setFinanskostnadData(null));
        axios.get(`${API}/naboar/${kommuneCode}`).then((res) => setNaboarData(res.data)).catch(() => setNaboarData(null));
        axios.get(`${API}/endringar/${kommuneCode}`)
          .then((res) => { setEndringsMerke(res.data); setEndringarData(res.data); })
          .catch(() => { clearEndringsMerke(); setEndringarData(null); });
        axios.get(`${API}/korttid/${kommuneCode}`).then((res) => setKorttidData(res.data)).catch(() => setKorttidData(null));
        axios.get(`${API}/boligpriser/${kommuneCode}`).then((res) => setBoligpriserData(res.data)).catch(() => setBoligpriserData(null));
        if (!eigeFylke) {
          axios.get(`${API}/gebyrer/averages/${snittKode}`)
            .then((res) => setGebyrAverages({ ...res.data, fylkeName: res.data.fylke_name || kommuneRes.data.fylke_name }))
            .catch(() => setGebyrAverages(null));
        } else {
          setGebyrAverages(null);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [kommuneCode]);

  const handleRefreshData = async () => {
    try {
      setRefreshing(true);
      await axios.post(`${API}/finance/${kommuneCode}/refresh`);
      toast.success("Data blir oppdatert i bakgrunnen...", {
        description: "Ver venleg og vent medan vi hentar nye data frå SSB.",
        duration: 5000,
      });
      
      // Poll for updated data every 2 seconds, up to 30 seconds
      let attempts = 0;
      const maxAttempts = 15;
      
      const pollForData = async () => {
        attempts++;
        try {
          const [kommuneRes, financeRes, populationRes, demographicsRes, oversiktRes, gebyrRes, bokostnadRes] = await Promise.all([
            axios.get(`${API}/kommuner/${kommuneCode}`),
            axios.get(`${API}/finance/${kommuneCode}`),
            axios.get(`${API}/population/${kommuneCode}`),
            axios.get(`${API}/demographics/${kommuneCode}`),
            axios.get(`${API}/okonomisk-oversikt/${kommuneCode}`).catch(() => ({ data: [] })),
            axios.get(`${API}/gebyrer/${kommuneCode}`).catch(() => ({ data: [] })),
            axios.get(`${API}/bokostnad/${kommuneCode}`).catch(() => ({ data: null })),
          ]);
          setOversiktData(oversiktRes.data);
          setGebyrData(gebyrRes.data);
          setBokostnadData(bokostnadRes.data);
          
          // Check if we have more data than before
          const newFinanceData = financeRes.data;
          const hasMoreData = newFinanceData.length > financeData.length ||
            newFinanceData.some((d, i) => {
              const oldData = financeData.find(f => f.year === d.year);
              if (!oldData) return true;
              // Check if we got new balance data
              return (d.anleggsmidler && !oldData.anleggsmidler) ||
                     (d.laan && !oldData.laan) ||
                     (d.eiendomsskatt_total && !oldData.eiendomsskatt_total);
            });
          
          if (hasMoreData || attempts >= maxAttempts) {
            // Update ALL state including kommune data (ordfører, styringsparti, etc.)
            setKommune(kommuneRes.data);
            setFinanceData(newFinanceData);
            setPopulationData(populationRes.data);
            setDemographicsData(demographicsRes.data);
            setRefreshing(false);
            if (hasMoreData) {
              toast.success("Data oppdatert!", {
                description: `Henta data for ${newFinanceData.length} år.`,
              });
            }
          } else if (attempts < maxAttempts) {
            setTimeout(pollForData, 2000);
          } else {
            // Even if no new finance data, update kommune and demographics
            setKommune(kommuneRes.data);
            setDemographicsData(demographicsRes.data);
            setRefreshing(false);
          }
        } catch (error) {
          console.error("Error polling for data:", error);
          if (attempts >= maxAttempts) {
            setRefreshing(false);
          } else {
            setTimeout(pollForData, 2000);
          }
        }
      };
      
      // Start polling after initial delay
      setTimeout(pollForData, 3000);
      
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Kunne ikkje oppdatere data");
      setRefreshing(false);
    }
  };
  
  // Admin edit functions
  const openEditDialog = () => {
    setEditForm({
      ordforar: kommune?.ordforar || "",
      styringsparti: kommune?.styringsparti || [],
    });
    setEditDialogOpen(true);
  };
  
  const handlePartyToggle = (partyName) => {
    setEditForm((prev) => ({
      ...prev,
      styringsparti: prev.styringsparti.includes(partyName)
        ? prev.styringsparti.filter((p) => p !== partyName)
        : [...prev.styringsparti, partyName],
    }));
  };
  
  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/kommuner/${kommuneCode}`, editForm, { withCredentials: true });
      toast.success("Kommune oppdatert");
      setEditDialogOpen(false);
      fetchData(); // Refresh kommune data
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Feil ved lagring");
    } finally {
      setSaving(false);
    }
  };
  
  // Generate Wikimedia Commons URL for coat of arms
  const getCoatOfArmsUrl = (kommuneName) => {
    if (!kommuneName) return null;
    // Normalize the name for Wikimedia Commons file naming
    const normalizedName = kommuneName
      .replace(/æ/g, 'æ')
      .replace(/ø/g, 'ø')
      .replace(/å/g, 'å')
      .replace(/Æ/g, 'Æ')
      .replace(/Ø/g, 'Ø')
      .replace(/Å/g, 'Å');
    // Wikimedia Commons uses URL encoding
    const encodedName = encodeURIComponent(normalizedName);
    return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodedName}_komm.svg`;
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPartyLogo = (partyName) => {
    const party = parties.find(
      (p) =>
        p.name.toLowerCase() === partyName.toLowerCase() ||
        p.short_name.toLowerCase() === partyName.toLowerCase()
    );
    return party?.logo_url;
  };

  // Prepare chart data

  const latestData = financeData.length > 0 ? financeData[financeData.length - 1] : null;
  const driftsmarginNum = latestData && latestData.inntekt && latestData.kostnad
    ? ((latestData.inntekt - latestData.kostnad) / latestData.inntekt) * 100
    : null;
  const driftsmargin = driftsmarginNum == null ? null : dec(driftsmarginNum, 2);
  // Total gjeld = kortsiktig gjeld (KG103) + langsiktig lån (KG100)
  const totalGjeld = latestData ? (latestData.gjeld || 0) + (latestData.laan || 0) : null;
  const gjeldsandel = latestData && latestData.anleggsmidler && totalGjeld
    ? dec(((totalGjeld / latestData.anleggsmidler) * 100), 2)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="animate-pulse text-[#4B647F]">Lastar kommunedata...</div>
      </div>
    );
  }

  if (!kommune) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FB]">
        <div className="text-center">
          <p className="text-[#D35E4E] mb-4">Kommune ikkje funne</p>
          <Button onClick={() => navigate("/dashboard")}>Tilbake til dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E9F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {/* Kommune Coat of Arms */}
              <div className="w-10 h-10 flex-shrink-0">
                <img
                  src={getCoatOfArmsUrl(kommune.kommune_name)}
                  alt={`${kommune.kommune_name} kommunevåpen`}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-[#1F2A36]">
                  {kommune.kommune_name}
                </h1>
                <p className="text-sm text-[#8AA4BF]">{kommune.fylke_name}</p>
              </div>
              {finanskostnadData?.gjeldsvekst?.varsel && (
                <button
                  onClick={() => document.getElementById("finanskostnader")?.scrollIntoView({ behavior: "smooth" })}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#E52437] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#c41d2d]"
                  title={`Lånegjelda per innbyggjar har vakse ${finanskostnadData.gjeldsvekst.vekst_pct} % sidan ${finanskostnadData.gjeldsvekst.fra_aar}`}
                  data-testid="header-gjeldsvarsel"
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                  Gjeldsvarsel: +{Math.round(finanskostnadData.gjeldsvekst.vekst_pct)} % sidan{" "}
                  {finanskostnadData.gjeldsvekst.fra_aar}
                </button>
              )}
            </div>

            <div className="flex items-center gap-4">
              <DelKnapp kommuneCode={kommune.kommune_code} kommuneNamn={kommune.kommune_name} tema="oversikt" testId="del-oversikt" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/sammenlign")}
                data-testid="compare-btn"
                className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Samanlikn
              </Button>

              {naboarData?.naboar?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/sammenlign?kommuner=${[kommuneCode, ...naboarData.naboar.slice(0, 4).map((n) => n.kommune_code)].join(",")}`)}
                  data-testid="compare-naboar-btn"
                  className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
                  title={naboarData.naboar.map((n) => n.kommune_name).join(", ")}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {naboarData.naboar[0]?.naermaste ? "Næraste kommunar" : "Naboane"}
                </Button>
              )}

              {naboarData?.naboar?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/topplister?nabo=${kommuneCode}`)}
                  data-testid="naboliste-btn"
                  className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
                  title={`Ranger ${kommune.kommune_name} mot nabokommunane`}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Naboliste
                </Button>
              )}

              {naboarData?.storby && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/sammenlign?kommuner=${naboarData.storby_kodar.join(",")}`)}
                  data-testid="compare-storby-btn"
                  className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Storbyane
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleRefreshData}
                disabled={refreshing}
                data-testid="refresh-data-btn"
                className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Oppdaterer..." : "Oppdater data"}
              </Button>

              {user?.role === "admin" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditDialog}
                  data-testid="edit-kommune-btn"
                  className="border-[#103766] text-[#103766] hover:bg-[#103766] hover:text-white"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Rediger
                </Button>
              )}
              {user ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  data-testid="logout-btn"
                  className="text-[#4B647F] hover:text-[#D35E4E]"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={login}
                  data-testid="login-btn"
                  className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Logg inn
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SectionNav />

        {endringarData && (
          <div className="mb-6">
            <EndringarNotice data={endringarData} />
          </div>
        )}

        {/* ================= KOMMUNENS ØKONOMI ================= */}
        <section id="okonomi" data-testid="section-okonomi">
          <SectionHeading icon={Landmark} title="Kommunen og økonomien" subtitle="Rekneskap, inntekter, utgifter, gjeld og investeringar for kommunekonsernet" />
          <div id="nokkeltall">
        {/* Overview Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Kommune Info Card */}
          <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-1">
            <CardHeader className="border-b border-[#E6E9F0] pb-4">
              <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#103766]" />
                Kommuneinfo
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Styringsparti */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">
                  Styringsparti
                </p>
                {kommune.styringsparti && kommune.styringsparti.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {kommune.styringsparti.map((party) => {
                      const match = parties.find(
                        (p) =>
                          p.name.toLowerCase() === party.toLowerCase() ||
                          p.short_name.toLowerCase() === party.toLowerCase()
                      );
                      const logoUrl = getPartyLogo(party);
                      const partyShortName = match?.short_name || party;

                      return (
                        <div
                          key={party}
                          data-testid={`styringsparti-${partyShortName.toLowerCase()}`}
                          className={`flex items-center gap-2 rounded-md border border-[#E6E9F0] bg-white px-2.5 py-1.5 ${
                            match?.logo_type === "wordmark" ? "w-full" : ""
                          }`}
                          title={match?.name || party}
                        >
                          {logoUrl ? (
                            <>
                              <img
                                src={logoUrl}
                                alt={match?.name || party}
                                className={`object-contain ${
                                  match?.logo_type === "wordmark"
                                    ? "w-[150px] h-auto max-h-14"
                                    : "h-7 w-auto max-w-[36px]"
                                }`}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextElementSibling.style.display = "inline-flex";
                                }}
                              />
                              <span
                                className="h-6 items-center rounded px-2 text-xs font-semibold text-white"
                                style={{ display: "none", backgroundColor: match?.color || "#103766" }}
                              >
                                {partyShortName}
                              </span>
                              {match?.logo_type === "symbol" && (
                                <span className="text-sm text-[#1F2A36]">{match.name}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-[#1F2A36]">{match?.name || party}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-[#4B647F]">Ikkje registrert</p>
                )}
              </div>

              {/* Ordførar */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
                  Ordførar
                </p>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-[#4B647F]" />
                  <p className="text-[#1F2A36] font-medium">
                    {kommune.ordforar 
                      ? `${kommune.ordforar}${kommune.ordforar_parti ? ` (${kommune.ordforar_parti})` : ''}`
                      : "Ikkje registrert"}
                  </p>
                </div>
              </div>

              {/* Kommune Code */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">
                  Kommunenummer
                </p>
                <Badge variant="outline">{kommune.kommune_code}</Badge>
              </div>

              {/* Source */}
              <div className="pt-2 border-t border-[#E6E9F0]">
                <p className="text-xs text-[#8AA4BF]">
                  Kjelde:{" "}
                  <a 
                    href={`https://no.wikipedia.org/wiki/${encodeURIComponent(kommune.kommune_name)}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-[#103766] hover:underline inline-flex items-center gap-1"
                  >
                    Wikipedia <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Key Metrics - moved before Eiendomsskatt */}
          <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-1">
            <CardHeader className="border-b border-[#E6E9F0] pb-4">
              <CardTitle className="text-lg font-medium text-[#1F2A36]">
                Nøkkeltal {latestData?.year || ""}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                    Inntekter
                  </p>
                  <p className="text-xl font-light text-[#1F2A36]">
                    {formatNumber(latestData?.inntekt)}
                  </p>
                  <p className="text-xs text-[#8AA4BF]">tusen kr</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                    Utgifter
                  </p>
                  <p className="text-xl font-light text-[#1F2A36]">
                    {formatNumber(latestData?.kostnad)}
                  </p>
                  <p className="text-xs text-[#8AA4BF]">tusen kr</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                    Driftsmargin
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-light text-[#1F2A36]">
                      {driftsmargin ? `${driftsmargin}%` : "—"}
                    </p>
                    {driftsmarginNum != null && driftsmarginNum >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-[#3F8A86]" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-[#D35E4E]" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                    Investeringar
                  </p>
                  <p className="text-xl font-light text-[#1F2A36]">
                    {formatNumber(latestData?.investeringer)}
                  </p>
                  <p className="text-xs text-[#8AA4BF]">tusen kr</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                    Eigedelar
                  </p>
                  <p className="text-xl font-light text-[#1F2A36]">
                    {formatNumber(latestData?.anleggsmidler)}
                  </p>
                  <p className="text-xs text-[#8AA4BF]">tusen kr</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                    Total gjeld
                  </p>
                  <p className="text-xl font-light text-[#1F2A36]">
                    {formatNumber(totalGjeld)}
                  </p>
                  <p className="text-xs text-[#8AA4BF]">tusen kr</p>
                  {latestData?.laan > 0 && latestData?.gjeld > 0 && (
                    <p className="text-xs text-[#4B647F] mt-1">
                      (Lån: {formatNumber(latestData.laan)} + Gjeld: {formatNumber(latestData.gjeld)})
                    </p>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                    Gjeldsandel
                  </p>
                  <p className="text-xl font-light text-[#1F2A36]">
                    {gjeldsandel ? `${gjeldsandel}%` : "—"}
                  </p>
                  <p className="text-xs text-[#4B647F]">av eigedelar (Total gjeld / Eigedelar)</p>
                </div>
                <div className="col-span-2 pt-2 border-t border-[#E6E9F0]">
                  <p className="text-xs text-[#8AA4BF]">
                    Kjelde:{" "}
                    <a href="https://www.ssb.no/statbank/table/12858" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                      SSB 12858 <ExternalLink className="w-3 h-3" />
                    </a>
                    {" / "}
                    <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                      13545 <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Eiendomsskatt Card - moved after Nøkkeltall */}
          <Card className="bg-white border border-[#E6E9F0] rounded-lg">
            <CardHeader className="border-b border-[#E6E9F0] pb-4">
              <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                <Landmark className="w-5 h-5 text-[#103766]" />
                Eigedomsskatt {latestData?.year || ""}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {latestData?.har_eiendomsskatt === 0 ? (
                <div className="text-center py-4">
                  <Badge variant="outline" className="text-[#3F8A86] border-[#3F8A86]">
                    Ingen eigedomsskatt
                  </Badge>
                  <p className="text-sm text-[#4B647F] mt-2">
                    Kommunen har ikkje innført eigedomsskatt
                  </p>
                </div>
              ) : latestData?.eiendomsskatt_total || latestData?.eiendomsskatt_type ? (
                <div className="space-y-4">
                  {/* Type of property tax */}
                  {latestData?.eiendomsskatt_type && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                        Type eigedomsskatt
                      </p>
                      <Badge variant="secondary" className="text-sm">
                        {latestData.eiendomsskatt_type}
                      </Badge>
                    </div>
                  )}

                  {/* Tax rates */}
                  <div className="grid grid-cols-2 gap-4">
                    {latestData?.generell_skattesats != null && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                          Generell sats
                        </p>
                        <p className="text-lg font-light text-[#1F2A36]">
                          {num(latestData.generell_skattesats, 2)} ‰
                        </p>
                      </div>
                    )}
                    {latestData?.diff_skattesats_bolig != null && latestData.diff_skattesats_bolig > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                          Bustadsats
                        </p>
                        <p className="text-lg font-light text-[#1F2A36]">
                          {num(latestData.diff_skattesats_bolig, 2)} ‰
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Bunnfradrag */}
                  {latestData?.har_bunnfradrag === 1 && latestData?.bunnfradrag_kr > 0 && (
                    <div className="pt-3 border-t border-[#E6E9F0]">
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                        Bunnfradrag
                      </p>
                      <p className="text-lg font-light text-[#1F2A36]">
                        {formatNumber(latestData.bunnfradrag_kr)} kr
                      </p>
                    </div>
                  )}

                  {/* Average tax */}
                  {latestData?.gjsnitt_skatt_bolig > 0 && (
                    <div className="pt-3 border-t border-[#E6E9F0]">
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                        Gjennomsnittleg årleg skatt (bustad)
                      </p>
                      <p className="text-xl font-light text-[#1F2A36]">
                        {formatNumber(latestData.gjsnitt_skatt_bolig)} kr
                      </p>
                    </div>
                  )}

                  {/* Total income */}
                  {latestData?.eiendomsskatt_total > 0 && (
                    <div className="pt-3 border-t border-[#E6E9F0]">
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                        Totale inntekter
                      </p>
                      <p className="text-xl font-light text-[#1F2A36]">
                        {formatNumber(latestData.eiendomsskatt_total)}
                      </p>
                      <p className="text-xs text-[#8AA4BF]">tusen kr</p>
                      
                      {(latestData.eiendomsskatt_bolig > 0 || latestData.eiendomsskatt_annen > 0) && (
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          {latestData.eiendomsskatt_bolig > 0 && (
                            <span className="text-[#4B647F]">
                              Bustad: {formatNumber(latestData.eiendomsskatt_bolig)}
                            </span>
                          )}
                          {latestData.eiendomsskatt_annen > 0 && (
                            <span className="text-[#4B647F]">
                              Anna: {formatNumber(latestData.eiendomsskatt_annen)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="pt-3 mt-3 border-t border-[#E6E9F0]">
                    <p className="text-xs text-[#8AA4BF]">
                      Kjelde:{" "}
                      <a href="https://www.ssb.no/statbank/table/12364" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                        SSB 12364 <ExternalLink className="w-3 h-3" />
                      </a>
                      {" / "}
                      <a href="https://www.ssb.no/statbank/table/14674" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                        14674 <ExternalLink className="w-3 h-3" />
                      </a>
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#4B647F]">
                  Data ikkje tilgjengeleg. Trykk "Oppdater data" for å hente.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
          </div>
          <div id="drift">
            <KommuneCharts financeData={financeData} populationData={populationData} variant="finance" />
          </div>
          <div id="inntektskilder">
            <RevenueSection data={oversiktData} averages={averagesFor("revenue")} />
          </div>
          <div id="driftsutgifter">
        <BreakdownSection
          data={oversiktData}
          section="expenses"
          sumKey="sum_netto_driftsutgifter"
          title="Netto driftsutgifter per tenesteområde"
          subtitle="Hovudgrupper (tenesteområde) frå KOSTRA, kommunekonsern (1000 kr)"
          sumLabel="Sum netto driftsutgifter"
          icon={Wallet}
          testId="expenses"
          sourceTable="12362"
          sourceNote="Negative tal tyder at området har høgare inntekter (f.eks. gebyr) enn utgifter. Andel blir rekna ut av summen av område med netto utgift."
          averages={averagesFor("expenses")}
        />
          </div>
          <div id="investeringer">
        <BreakdownSection
          data={oversiktData}
          section="investment"
          sumKey="sum_investeringsutgifter"
          title="Investeringar – brutto investeringsutgifter per tenesteområde"
          subtitle="Hovudgrupper (tenesteområde) frå KOSTRA, kommunekonsern (1000 kr)"
          sumLabel="Sum investeringsutgifter"
          icon={HardHat}
          testId="investment"
          sourceTable="12362"
          averages={averagesFor("investment")}
        />
          </div>
          <div id="arbeidskapital">
            <ArbeidskapitalChart data={arbeidskapitalData} kommuneName={kommune.kommune_name} alertYears={varselData?.years?.arbeidskapital} />
            <PremieavvikChart data={premieavvikData} kommuneName={kommune.kommune_name} />
          </div>
          <div id="disposisjonsfond">
            <DisposisjonsfondChart data={disposisjonsfondData} kommuneName={kommune.kommune_name} />
          </div>
          <div id="fond">
            <FondSection data={fondData} kommuneName={kommune.kommune_name} alertYears={varselData?.years?.fond} />
          </div>
          <div id="finanskostnader">
            <FinanskostnadSection data={finanskostnadData} kommuneCode={kommune.kommune_code} />
          </div>
          <div id="eiendom">
            <EiendomSection data={eiendomData} />
          </div>
          <div id="varsel">
            <VarselSection data={varselData} kommuneName={kommune.kommune_name} />
          </div>
          <div id="rangering">
            <RangeringSection data={rangeringData} />
          </div>
          <div id="detaljert">
            <FinanceDataTable financeData={financeData} />
          </div>
        </section>

        {/* ================= HELSE OG OMSORG ================= */}
        <section id="helse" className="mt-12" data-testid="section-helse">
          <SectionHeading icon={HeartPulse} title="Helse og omsorg" subtitle="Fastlegedekning, bemanning i omsorga og utviklinga i folketalet" />
          <div id="fastlege">
            <FastlegeSection data={fastlegeData} kommuneName={kommune.kommune_name} />
          </div>
          <div id="tjenester">
            <TjenesterSection data={tjenesterData} kommuneCode={kommune.kommune_code} />
          </div>
<KorttidSection data={korttidData} />
<KommuneCharts financeData={financeData} populationData={populationData} variant="population" />
        </section>

        {/* ================= BARN OG UNGE ================= */}
        <section id="barnunge" className="mt-12" data-testid="section-barnunge">
          <SectionHeading icon={Baby} title="Barn og unge" subtitle="Alderssamansetjing, barnehage og skule, og barn i låginntektsfamiliar" />
<AlderSection data={alderData} />
          <div id="ulikskap">
            <UlikskapSection data={ulikskapData} kommuneName={kommune.kommune_name} kommuneCode={kommune.kommune_code} />
          </div>
        </section>

        {/* ================= ARBEIDSLIV ================= */}
        <section id="arbeidsliv" className="mt-12" data-testid="section-arbeidsliv">
          <SectionHeading icon={Briefcase} title="Folk og arbeidsliv" subtitle="Befolkning og inntekt, sysselsetjing, ytingar, pendling og næringar" />
          <div id="befolkning">
            <KommuneDemographics demographicsData={demographicsData} income={incomeOverride} alder={alderData} />
          </div>
<ArbeidSection data={arbeidData} />
<PendlingSection data={pendlingData} />
        </section>

        {/* ================= BOLIG OG HUSHOLDNINGSØKONOMI ================= */}
        <section id="bustad" className="mt-12" data-testid="section-bustad">
          <SectionHeading icon={Home} title="Bustad og hushaldsøkonomi" subtitle="Hushaldsøkonomi, livsopphald, bustadprisar, leige og kommunale gebyr" />
          <div id="husholdning">
            <HusholdningSection data={husholdningData} />
          </div>
<LivsopphaldSection data={livsopphaldData} kommuneCode={kommune.kommune_code} />
<LevekostnadCard data={levekostnadData} />
          <div id="bolig">
            <BoligOverview bokostnad={bokostnadData} boligpriser={boligpriserData} leie={leieData} gebyrer={gebyrData} medianHouseholdIncome={incomeBase} incomeLabel={incomeLabel.charAt(0).toUpperCase() + incomeLabel.slice(1)} />
          </div>
          <BokostnadSection
            data={bokostnadData}
            medianHouseholdIncome={incomeBase}
            incomeLabel={incomeLabel}
            householdTypes={householdTypes}
            fylkeName={bokostnadData?.gruppe_namn ? "storbyane" : kommune?.fylke_name}
            rentMonthly={leieData?.average?.series?.slice(-1)[0]?.monthly}
            rentEstimate={leieData?.average?.estimate}
          />
<BoligpriserSection data={boligpriserData} />
<LeieSection data={leieData} />
<GebyrSection data={gebyrData} averages={gebyrAverages} kommuneCode={kommune.kommune_code} kommuneNamn={kommune.kommune_name} />
        </section>

        {/* ================= ARTIKLER ================= */}
        <section id="artikler" className="mt-12" data-testid="section-artikler">
          <ArticleSection kommuneCode={kommuneCode} user={user} />
        </section>
      </main>
      
      {/* Admin Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1F2A36]">
              <Shield className="w-5 h-5 text-[#103766]" />
              Rediger {kommune.kommune_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="ordforar" className="text-sm font-medium text-[#1F2A36]">
                Ordførar
              </Label>
              <Input
                id="ordforar"
                value={editForm.ordforar}
                onChange={(e) => setEditForm({ ...editForm, ordforar: e.target.value })}
                placeholder="Navn på ordførar"
                className="border-[#E6E9F0] bg-white text-[#1F2A36]"
              />
            </div>
            
            <div className="space-y-3">
              <Label className="text-sm font-medium text-[#1F2A36]">
                Styringsparti
              </Label>
              <div className="grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto border rounded-lg p-4 border-[#E6E9F0] bg-white">
                {parties.map((party) => (
                  <div key={party.name} className="flex items-center space-x-2">
                    <Checkbox
                      id={`party-${party.name}`}
                      checked={editForm.styringsparti.includes(party.name)}
                      onCheckedChange={() => handlePartyToggle(party.name)}
                      className="border-[#8AA4BF] data-[state=checked]:bg-[#103766] data-[state=checked]:border-[#103766]"
                    />
                    <label
                      htmlFor={`party-${party.name}`}
                      className="flex items-center gap-2 text-sm cursor-pointer text-[#1F2A36]"
                    >
                      {party.logo_url ? (
                        <img
                          src={party.logo_url}
                          alt={party.name}
                          className={`object-contain ${
                            party.logo_type === "wordmark"
                              ? "w-[120px] h-auto max-h-12"
                              : "h-7 w-auto max-w-[32px]"
                          }`}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : null}
                      {(!party.logo_url || party.logo_type === "symbol") && (
                        <span className="truncate">{party.name}</span>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-[#E6E9F0] text-[#4B647F]"
            >
              <X className="w-4 h-4 mr-2" />
              Avbryt
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-[#103766] hover:bg-[#0d2d52]"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Lagrer..." : "Lagre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* KOSTRA Data Dialog */}
      <Dialog open={kostraDialogOpen} onOpenChange={setKostraDialogOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-[#103766]" />
              KOSTRA-data for {kommune.kommune_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-auto max-h-[60vh] bg-white rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E6E9F0] bg-[#F6F8FB]">
                  <TableHead className="font-semibold text-[#1F2A36] sticky left-0 bg-[#F6F8FB]">År</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Driftsinntekter</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Driftsutgifter</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Investeringar</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Eigedelar</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Langsiktig lån</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Kortsiktig gjeld</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Total gjeld</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Premieavvik</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Eigedomsskatt totalt</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Eigedomsskatt bustad</TableHead>
                  <TableHead className="text-right font-semibold text-[#1F2A36]">Befolkning</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white">
                {financeData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-[#8AA4BF] py-8 bg-white">
                      Ingen KOSTRA-data tilgjengeleg
                    </TableCell>
                  </TableRow>
                ) : (
                  financeData.map((row) => {
                    const totalGjeld = (row.gjeld || 0) + (row.laan || 0);
                    const popData = populationData.find(p => p.year === row.year);
                    return (
                      <TableRow key={row.year} className="border-b border-[#E6E9F0] hover:bg-[#F6F8FB] bg-white">
                        <TableCell className="font-medium sticky left-0 bg-white">{row.year}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.inntekt)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.kostnad)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.investeringer)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.anleggsmidler)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.laan)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.gjeld)}</TableCell>
                        <TableCell className="text-right font-medium text-[#D35E4E] bg-white">{formatNumber(totalGjeld)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.premieavvik)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.eiendomsskatt_total)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(row.eiendomsskatt_bolig)}</TableCell>
                        <TableCell className="text-right bg-white">{formatNumber(popData?.population)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          
          <div className="text-xs text-[#8AA4BF] mt-4 space-y-1">
            <p><strong>Datakilde:</strong> Statistisk sentralbyrå (SSB) KOSTRA-statistikk</p>
            <p><strong>Tabellar:</strong> 12858 (Resultatrekneskap), 13545 (Balanse), 12882 (Befolkning), 12364/14674 (Eigedomsskatt)</p>
            <p><strong>Eining:</strong> Alle beløp i tusen kroner (1000 kr)</p>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setKostraDialogOpen(false)}
              className="border-[#E6E9F0]"
            >
              Lukk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KommuneView;
